import { ZodError } from "zod"
import { classifyRawError, RawspeedError } from "@/lib/raw/raw-errors"
import { AxiosError, isAxiosError } from "axios"

export class AbortError extends Error {}

export const getAxiosBackendReason = (error: unknown, options?: { status?: number | number[] }) => {
	if (!isAxiosError(error)) {
		return undefined
	}

	if (options?.status !== undefined) {
		const allowedStatuses = Array.isArray(options.status) ? options.status : [options.status]
		if (!allowedStatuses.includes(error.response?.status ?? 0)) {
			return undefined
		}
	}

	const responseData: unknown = error.response?.data
	if (typeof responseData !== "object" || responseData == null || !("detail" in responseData)) {
		return undefined
	}

	const { detail } = responseData
	if (typeof detail !== "object" || detail == null || !("reason" in detail)) {
		return undefined
	}

	return typeof detail.reason === "string" ? detail.reason : undefined
}

type ErrorPredicate = (error: unknown) => boolean
type ErrorMessageGenerator = (error: unknown) => string

export class ErrorMap extends Map<ErrorPredicate, ErrorMessageGenerator | string> {
	defaultMessage: string

	constructor(
		entries: ReadonlyArray<[ErrorPredicate, ErrorMessageGenerator | string]>,
		defaultMessage: string,
	) {
		super(entries)
		this.defaultMessage = defaultMessage
	}

	getErrorMessage(error: unknown): string {
		for (const [predicate, messageGenerator] of this.entries()) {
			if (predicate(error)) {
				return typeof messageGenerator === "function"
					? messageGenerator(error)
					: messageGenerator
			}
		}

		return error instanceof Error ? error.message : this.defaultMessage
	}
}

export const isAbortError = (error: unknown) => {
	if (error instanceof DOMException && error.name === "abort") {
		return true
	}

	if (error === "abort") {
		return true
	}

	if (error instanceof AbortError) {
		return true
	}
	return error instanceof Error && error.name === "AbortError"
}

export const isNotFoundError = (error: unknown) => {
	return (
		(typeof DOMException !== "undefined" &&
			error instanceof DOMException &&
			error.name === "NotFoundError") ||
		(error instanceof Error && error.name === "NotFoundError")
	)
}

export const isBrowserStorageQuotaError = (error: unknown) => {
	return (
		error instanceof Error &&
		error.name === "AbortError" &&
		error.message?.includes("QuotaExceededError")
	)
}

export const isStorageQuotaError = (error: unknown) => {
	if (error instanceof Error && error.name === "QuotaExceededError") {
		return true
	}

	const message = error instanceof Error ? error.message.toLowerCase() : ""

	if (
		message.includes("quotaexceedederror") ||
		message.includes("quota exceeded") ||
		message.includes("storage quota")
	) {
		return true
	}

	if (
		error instanceof Error &&
		(error.name === "NotReadableError" || error.message?.includes("NotReadableError"))
	) {
		return "maybe"
	}

	return false
}

export const isUnsupportedLRTemplateError = (error: unknown) => {
	return error instanceof ZodError && error.issues.some((issue) => issue.path.includes("type"))
}

export const isJsonParseError = (error: unknown) => {
	return error instanceof SyntaxError && error.message.includes("JSON")
}

export const isSqliteNotADatabaseError = (error: unknown) => {
	let currentError: unknown = error
	let depth = 0

	while (depth < 4 && currentError && typeof currentError === "object") {
		const candidate = currentError as {
			name?: unknown
			code?: unknown
			resultCode?: unknown
			message?: unknown
			cause?: unknown
		}
		const message = typeof candidate.message === "string" ? candidate.message.toLowerCase() : ""

		if (
			// Comlink strips `cause` across the worker boundary, so the wrapper's name is all that survives.
			candidate.name === "InvalidCatalogDatabaseError" ||
			candidate.code === "SQLITE_NOTADB" ||
			candidate.code === 26 ||
			candidate.resultCode === 26 ||
			message.includes("sqlite_notadb") ||
			message.includes("not a database") ||
			message.includes("result code 26")
		) {
			return true
		}

		currentError = candidate.cause
		depth++
	}

	return false
}

export const isUnsupportedFileError = (error: unknown) => {
	return classifyRawError(error) === RawspeedError.UnsupportedFormat
}

export const isCorruptedFileError = (error: unknown) => {
	return classifyRawError(error) === RawspeedError.Corrupted
}

export const getBackendErrorMessage = (error: unknown) => {
	const errorMessage =
		error instanceof AxiosError
			? (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message
			: undefined

	return errorMessage
}

export const isAccountDelinquentError = (error: unknown) => {
	if (!(error instanceof AxiosError)) {
		return false
	}
	if (error.response?.status !== 403) {
		return false
	}

	const errorMessage = getBackendErrorMessage(error)

	return !!errorMessage && errorMessage.includes("unpaid")
}
